<template>
    <ModalBase
        :name="'cambiar-precio-' + abonado.id"
        :title="'Cambiar tipo de abono y precio de ' + abonado.name + ' ' + abonado.surnames"
        @close="() => close(false)"
    >
        <ValidationObserver ref="observer" tag="form" @submit.prevent="submit()">
            <div class="tw:flex tw:flex-col tw:gap-6">

                <!-- Card 1: Información del Abonado (solo lectura) -->
                <Card title="Información del Abonado">
                    <div class="tw:flex tw:flex-col tw:gap-3">
                        <DataField label="Nº de abonado" :value="abonado.n_abonado || '-'" />
                        <DataField label="Nombre" :value="abonado.name || '-'" />
                        <DataField label="Apellidos" :value="abonado.surnames || '-'" />
                        <DataField label="Zona" :value="abonado.asiento?.region || '-'" />
                        <DataField label="Fila" :value="abonado.asiento?.row || '-'" />
                        <DataField label="Butaca" :value="abonado.asiento?.seat || '-'" />
                    </div>
                </Card>

                <!-- fieldset: Seleccionar Tipo y Precio -->
                <fieldset class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4">
                    <legend class="tw:text-sm tw:font-semibold tw:text-gray-900 tw:px-2">
                        Seleccionar Tipo y Precio
                    </legend>
                    <div class="tw:flex tw:flex-col tw:gap-4">

                        <BaseMultiselect
                            v-model="tipo_abono"
                            name="tipo de abono"
                            label="Tipo de abono"
                            rules="required"
                            :options="tiposOptions"
                            value-key="_uid"
                            label-key="_label"
                        >
                            <template #option="{ option }">
                                {{ option._label }}
                            </template>
                            <template #singleLabel="{ option }">
                                {{ option._label }}
                            </template>
                        </BaseMultiselect>

                        <BaseMultiselect
                            v-model="precio"
                            name="precio"
                            label="Precio"
                            rules="required"
                            :options="preciosOptions"
                            value-key="_uid"
                            label-key="_label"
                            :disabled="tipo_abono === null || tipo_abono === undefined"
                        >
                            <template #option="{ option }">
                                {{ option._label }}
                            </template>
                            <template #singleLabel="{ option }">
                                {{ option._label }}
                            </template>
                        </BaseMultiselect>

                    </div>
                </fieldset>

                <!-- fieldset: Resumen del Cambio -->
                <fieldset
                    v-if="precio"
                    class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4"
                >
                    <legend class="tw:text-sm tw:font-semibold tw:text-gray-900 tw:px-2">
                        Resumen del Cambio
                    </legend>
                    <div class="tw:flex tw:flex-col tw:gap-4">

                        <div :class="[
                            'tw:rounded-lg tw:p-4 tw:border',
                            cambio > 0
                                ? 'tw:bg-blue-50 tw:border-blue-200'
                                : 'tw:bg-red-50 tw:border-red-200'
                        ]"
                        >
                            <p class="tw:text-sm tw:text-gray-600 tw:mb-1">
                                <span v-if="cambio > 0" class="tw:font-semibold">Este cambio supone un ingreso de:</span>
                                <span v-else class="tw:font-semibold">Este cambio supone una devolución de:</span>
                            </p>
                            <p :class="[
                                'tw:text-2xl tw:font-bold',
                                cambio > 0 ? 'tw:text-blue-600' : 'tw:text-red-600'
                            ]"
                            >
                                {{ cambio | formatear_precio }}
                            </p>
                        </div>

                        <BaseMultiselect
                            v-model="metodo_pago"
                            name="método de pago"
                            label="Método de pago/devolución"
                            :options="metodos_pago"
                            value-key="id"
                            label-key="nombre"
                            placeholder="Selecciona un método"
                        />

                    </div>
                </fieldset>

                <!-- Botones -->
                <div class="tw:flex tw:gap-3 tw:justify-end">
                    <Button variant="ghost" @click="close(false)">CANCELAR</Button>
                    <Button
                        type="submit"
                        variant="primary"
                        :loading="loading"
                        :disabled="tipo_abono === null || tipo_abono === undefined || precio === null || precio === undefined"
                    >CAMBIAR</Button>
                </div>

            </div>
        </ValidationObserver>
    </ModalBase>
</template>

<script setup>
    import { ref } from 'vue';
    import ModalBase from '@/modules/admin/ui/ModalBase.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import DataField from '@/modules/admin/ui/DataField.vue';
    import { useCambiarPrecioLogic } from '@/composables/admin/abonados/useCambiarPrecioLogic';

    const props = defineProps({
        abonado: {
            type: Object,
            required: true
        }
    });

    const emit = defineEmits(['close']);

    const {
        loading,
        tipo_abono,
        precio,
        metodo_pago,
        metodos_pago,
        tiposOptions,
        preciosOptions,
        cambio,
        metodosPagoDisponibles,
        getTipos,
        cambiar,
        reset
    } = useCambiarPrecioLogic(props.abonado, null, null, {
        onSuccess: cambiado => close(cambiado)
    });

    const observer = ref(null);

    async function submit() {
        const isValid = await observer.value.validate();
        if (isValid) {
            await cambiar();
        } else {
            close(false);
        }
    }

    function close(cambiado) {
        reset();
        emit('close', 'cambiar-precio-' + props.abonado.id, cambiado);
    }

    // Init
    getTipos();
    metodos_pago.value = metodosPagoDisponibles.value.slice();
</script>
